<template>
  <div class="flex flex-col md:flex-row gap-4 px-2">
    <div class="filter-column filter-column--calendar md:pt-2">
      <div class="label">{{ $t('search.filters.calendarLabel') }}</div>
      <FilterItemDate
        :selected-dates="props.dates"
        @updated="
          ($event) => {
            $emit('updated', {
              dates: $event,
            })
          }
        "
      />
    </div>
    <div
      class="filter-column filter-column--options md:pt-2 flex flex-col [&>div]:flex [&>div]:justify-between gap-4"
    >
      <div class="flex items-center">
        <div class="label">{{ $t('search.filters.peopleAmountLabel') }}</div>
        <NumberControlButtons
          v-model="peopleAmount"
          :min="1"
          :max="1000"
          :disable-minus="peopleAmount === 1"
        />
      </div>

      <div class="flex flex-col">
        <div class="label">{{ $t('search.filters.classType') }}</div>
        <div class="mt-1 flex flex-wrap gap-1">
          <Chip
            v-for="item in otherFilters"
            :key="`kft-search-language-${item.key}`"
            :label="item.label"
            :selected="typeOfClass === item.key"
            class="chip-filter"
            @click="clickOtherFilter(item.key)"
          >
            {{ item.label }}
          </Chip>
        </div>
      </div>

      <div class="flex flex-col">
        <div class="label">{{ $t('search.selectFiltersComponent.price') }}</div>
        <div class="w-full mt-3 mx-0 price-slider">
          <Slider
            ref="sliderRange"
            v-model="price"
            :max="PRICE_MAX"
            :lazy="false"
            :format="format"
            :tooltips="false"
            :classes="sliderClasses"
            :options="sliderOptions"
            @change="
              (e: [number, number]) =>
                $emit('updated', { priceMin: e[0], priceMax: e[1] })
            "
          />
          <div class="grid grid-cols-2 gap-8">
            <div class="w-3/4">
              <input
                v-model="price[0]"
                type="number"
                class="border rounded-md py-0 px-2 mx-0 mt-4 w-full h-8"
                @input="
                  $emit('updated', { priceMin: price[0], priceMax: price[1] })
                "
              />
              <div class="text-sm text-gray-600 text-center">Min</div>
            </div>
            <div class="w-3/4 ml-auto">
              <input
                v-model="price[1]"
                type="number"
                class="border rounded-md py-0 px-2 mx-0 mt-4 w-full h-8"
                @input="
                  $emit('updated', { priceMin: price[0], priceMax: price[1] })
                "
              />
              <div class="text-sm text-gray-600 text-center">Max</div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col">
        <div class="label">
          {{
            $t('search.selectFiltersComponent.showEventsInPreferredLanguage')
          }}
        </div>
        <div v-if="availableLanguages" class="gap-1 flex flex-wrap mt-1">
          <Chip
            v-for="(language, code) in availableLanguages"
            :key="`kft-search-language-${code}`"
            :label="language"
            :selected="languages.includes(code)"
            class="chip-filter"
            @click="$emit('updated', { languages: code })"
            >{{ language }}
          </Chip>
          <!-- @pressed="fnToggleFacet('languages', code)" -->
          <!-- :value="temporaryFilters.languages.indexOf(code) !== -1" -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// eslint-disable-next-line import/default
import Slider from '@vueform/slider'
import { fnGetAvailableLanguages } from '~/utils/localeHelpers'
import { formatMoney } from '~/utils/currencyHelpers'

const PRICE_MIN = 0
const PRICE_MAX = 1200

const { t } = useI18n()
const sliderRange = ref<InstanceType<typeof Slider> | null>(null)

onMounted(() => {
  if (sliderRange.value?.$el) {
    sliderRange.value.$el
      .querySelectorAll('.tooltip-text')
      .forEach((item: Element, index: number) => {
        item.classList.add('tooltip-text--' + (index + 1))
      })
  }
})

const props = withDefaults(
  defineProps<{
    dates?: []
    hasAvailableDates?: boolean
    languages?: string[]
    priceMin?: number
    priceMax?: number
    peopleAmount?: number
    typeOfClass?: string
  }>(),
  {
    dates: () => [],
    hasAvailableDates: false,
    languages: () => [],
    priceMin: PRICE_MIN,
    priceMax: PRICE_MAX,
    peopleAmount: 1,
    typeOfClass: undefined,
  }
)

const format = (value: number) => formatMoney(value)

const sliderOptions = {
  range: {
    min: [PRICE_MIN],
    '10%': [20, 1],
    '60%': [150, 1],
    '80%': [900, 1],
    max: [PRICE_MAX],
  },
}

const sliderClasses = {
  target:
    'relative box-border select-none touch-none tap-highlight-transparent touch-callout-none disabled:cursor-not-allowed',
  focused: 'slider-focused',
  tooltipFocus: 'slider-tooltip-focus',
  tooltipDrag: 'slider-tooltip-drag',
  horizontal: 'slider-horizontal h-1.5',
  base: 'w-full h-full relative z-1 bg-gray-300 rounded',
  connects: 'w-full h-full relative overflow-hidden z-0 rounded',
  connect:
    'absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full bg-teal-900 cursor-pointer tap:duration-300 tap:transition-transform disabled:bg-gray-400 disabled:cursor-not-allowed',
  origin:
    'slider-origin absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full h:h-0 v:-top-full txt-rtl-h:left-0 txt-rtl-h:right-auto v:w-0 tap:duration-300 tap:transition-transform',
  handle:
    'bg-teal-50 border border-teal-900 absolute rounded-full cursor-grab focus:outline-none h:w-[13px] h:h-[13px] h:-top-[4px] h:-right-[6px] txt-rtl-h:-left-[4px] txt-rtl-h:right-auto v:w-[13px] v:h-[13px] v:-top-[4px] v:-right-[4px] disabled:cursor-not-allowed focus:ring focus:ring-primary-active focus:ring-opacity-30',
  touchArea: 'h-full w-full',
  tooltip:
    'tooltip-text absolute block text-sm text-center font-semibold whitespace-nowrap text-gray-600',
  tooltipTop: 'bottom-4 merge-h:bottom-3',
  tooltipHidden: 'slider-tooltip-hidden',
  active: 'slider-active shadow-slider-active cursor-grabbing',
  draggable: 'cursor-ew-resize v:cursor-ns-resize',
  tap: 'slider-state-tap',
  drag: 'slider-state-drag',
}

const emit = defineEmits(['updated'])
const peopleAmount = ref(props.peopleAmount)

const typeOfClass = ref(props.typeOfClass)
const otherFilters = computed(() => [
  {
    key: 'publicEvents',
    label: t('search.filters.publicTickets'),
  },
  {
    key: 'directEvents',
    label: t('search.filters.privateGroups'),
  },
  {
    key: 'online',
    label: t('search.filters.onlineEvents'),
  },
])

watch(peopleAmount, (val) => emit('updated', { peopleAmount: val }))

const clickOtherFilter = (key: string) => {
  typeOfClass.value = typeOfClass.value === key ? undefined : key
  emit('updated', { typeOfClass: typeOfClass.value })
}

const i18n = useI18n()
const availableLanguages = fnGetAvailableLanguages(i18n)
const price = ref([props.priceMin ?? PRICE_MIN, props.priceMax ?? PRICE_MAX])
</script>

<style lang="postcss" scoped>
.filter-column {
  flex: 1 1 100%;
}

.label {
  @apply h-9 font-medium text-black flex items-center;
}

.chip-filter {
  @apply m-0;

  &:not(.chip--selected) {
    @apply hover:border-teal-900 hover:bg-white hover:text-gray-700;
  }
}

.price-slider {
  :deep(.tooltip-text--1) {
    left: 0;
  }

  :deep(.tooltip-text--2) {
    right: 0;
  }
}
</style>
